<script setup lang="ts">
/**
 * Приглашение на встречу (DEV-1947).
 *
 * Висит в каркасе, а не на «Встречах»: зовут человека там, где он есть, а он в
 * этот момент смотрит доску или задачу. Баннер на одном экране заметок увидел
 * бы только тот, кто и так собирался писать.
 *
 * Опрос раз в десять секунд — приглашение приходит редко, и держать ради него
 * живой поток в каждой вкладке дороже, чем один лёгкий запрос: поток встречи
 * открывается уже внутри мит-режима.
 */
import { useIntervalFn } from '@vueuse/core'
import { Users } from 'lucide-vue-next'
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { useAuthStore } from '@/features/auth/store'
import { useMeetingStore } from '@/features/meetings/store'

/** Как часто спрашиваем про приглашения */
const POLL_INTERVAL = 10_000

const { t } = useI18n()
const router = useRouter()
const auth = useAuthStore()
const meeting = useMeetingStore()

/** Первое приглашение: их редко бывает больше одного разом */
const invite = computed(() => meeting.invites[0] ?? null)

/**
 * Показывать ли баннер. Везде, где человека застали, — включая открытый
 * открытые «Встречи».
 *
 * Раньше на самой встрече баннер молчал: считалось, что приглашение видно в её
 * шапке. В шапке его не было никогда — она рисует только исходящие
 * приглашения. А ждут входящего ровно там: человек разворачивает ленту во весь
 * экран и садится записывать, пока его позовут. На проде из-за этого не
 * состоялась ни одна встреча — оба участника звали друг друга и не видели ни
 * одного зова (DEV-1949).
 */
const isVisible = computed(() => auth.isAuthenticated && !!invite.value)

const join = async () => {
  const pending = invite.value

  if (!pending) return

  await meeting.join(pending.sessionId)
  await router.push({ name: 'meeting', params: { sessionId: pending.sessionId } })
}

onMounted(() => {
  if (auth.isAuthenticated) void meeting.refreshState()
})

useIntervalFn(() => {
  if (auth.isAuthenticated) void meeting.refreshState()
}, POLL_INTERVAL)
</script>

<template>
  <div v-if="isVisible" class="invite" role="status">
    <Users :size="16" aria-hidden="true" />

    <span class="invite__text">
      {{ t('meetings.session.banner', { name: invite?.invitedByName || t('meetings.session.someone') }) }}
    </span>

    <button type="button" class="invite__join" @click="join()">
      {{ t('meetings.session.join') }}
    </button>
  </div>
</template>

<style scoped>
.invite {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-primary-tint);
  color: var(--color-primary);
}

.invite__text {
  font-size: var(--font-size-body-sm);
  line-height: 18px;
}

.invite__join {
  margin-left: auto;
  padding: 4px 12px;
  border: none;
  border-radius: 6px;
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-family: inherit;
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
  cursor: pointer;
}
</style>
